import Loader from 'react-loader-spinner'
import clsx from 'clsx'
import PropTypes from 'prop-types'
import rem from '@/utils/rem'

const Button = ({
  loading,
  prefix,
  suffix,
  children,
  warning,
  ghost,
  border,
  round,
  full,
  noborder,
  sizing = 'default',
  maxWidth,
  minWidth,
  noHover,
  dotted,
  error,
  btnText,
  customHover,
  style,
  nobackground = false,
  grey,
  addClass,
  selected,
  ascent,
  textWarning,
  warningbackgroundHover,
  disabled,
  ...btnProps
}) => {
  const sizingOptions = {
    DEFAULT: 'default',
    MEDIUM: 'medium',
    MINI: 'mini',
  }

  const btnStyles = {
    maxWidth: rem(maxWidth),
    minWidth: rem(minWidth),
    ...style,
  }

  const btnClass = clsx(
    // Static structure (always applied)
    'inline-flex items-center justify-center h-9 px-3 py-2 gap-1 font-avenir tracking-[0] cursor-pointer select-none text-center align-middle whitespace-nowrap no-underline border-2 rounded type-body-1 antialiased appearance-none transition-[background-color,border-color,color] duration-150 focus:outline-none disabled:cursor-not-allowed',
    addClass,
    // Sizing overrides
    sizing === sizingOptions.MEDIUM && 'p-5!',
    sizing === sizingOptions.MINI && 'h-[21px]! py-1! px-[10px]! text-[10px]! rounded-[5px]!',
    // Layout
    full && 'w-full',
    round && 'rounded-full',
    // Primary color scheme — mutually exclusive
    warning
      ? 'text-white bg-red-50 border-red-50 hover:text-red-50 hover:border-red-50 hover:bg-white'
      : ghost
        ? 'text-theme-50 border-theme-50 bg-transparent'
        : ascent
          ? selected
            ? 'text-theme-50 border-theme-95 bg-theme-95 hover:text-theme-50 hover:cursor-default hover:bg-theme-95 disabled:text-white disabled:border-grey-90 disabled:bg-transparent!'
            : 'text-grey-50 border-grey-90 bg-transparent hover:text-white hover:bg-theme-40 hover:[&_svg_path]:fill-white disabled:text-white disabled:border-grey-90 disabled:bg-transparent!'
          : border
            ? [
                'text-grey-10 border-[1px]! border-grey-90 bg-white disabled:text-white disabled:border-grey-90 disabled:bg-grey-90!',
                noHover && 'hover:text-grey-10 hover:border-grey-90 hover:bg-grey-90',
              ]
            : noborder
              ? [
                  'text-theme-50 border-transparent bg-transparent [&_svg_path]:fill-theme-50 disabled:text-white disabled:border-grey-90 disabled:bg-grey-90! disabled:[&_svg_path]:fill-white',
                  !customHover &&
                    '[&:hover:enabled]:text-theme-50 [&:hover:enabled]:border-transparent [&:hover:enabled]:bg-transparent',
                ]
              : btnText
                ? [
                    'max-w-[412px] text-theme-50 bg-transparent border-transparent disabled:bg-transparent! disabled:border-transparent! disabled:text-grey-70!',
                    !customHover &&
                      '[&:hover:enabled]:text-theme-40 [&:hover:enabled]:bg-transparent [&:hover:enabled]:border-transparent',
                  ]
                : dotted
                  ? 'text-theme-50 border-dashed border-theme-50 bg-transparent'
                  : [
                      // Default: solid theme background
                      'text-white bg-theme-50 border-transparent disabled:text-white disabled:border-grey-90 disabled:bg-grey-90!',
                      !customHover &&
                        !noHover &&
                        '[&:hover:enabled]:text-white [&:hover:enabled]:border-theme-40 [&:hover:enabled]:bg-theme-40',
                    ],
    // Additive overrides (stack on top of primary scheme)
    error && [
      'bg-red-50!',
      !customHover && '[&:hover:enabled]:border-red-40! [&:hover:enabled]:bg-red-40!',
    ],
    grey && 'bg-grey-50!',
    nobackground && 'bg-transparent!',
    textWarning && ['text-red-50!', !customHover && '[&:hover:enabled]:text-red-40!'],
    warningbackgroundHover && 'hover:text-white! hover:bg-red-50!',
    !border &&
      noHover &&
      'hover:text-theme-50! hover:border-0! hover:bg-transparent! [&_svg_path]:fill-theme-50!'
  )

  const Prefix = () =>
    prefix ? (
      <>
        {prefix}
        <span style={{ marginLeft: rem(8) }} />
      </>
    ) : null

  const Suffix = () =>
    suffix ? (
      <>
        <span style={{ marginLeft: rem(8) }} />
        {suffix}
      </>
    ) : null

  return (
    <button {...btnProps} className={btnClass} style={btnStyles} disabled={disabled || loading}>
      {loading ? (
        <Loader
          type="ThreeDots"
          color={btnText ? 'var(--color-theme-50)' : '#ffffff'}
          height={10}
          width={15}
        />
      ) : (
        <>
          <Prefix />
          {children}
          <Suffix />
        </>
      )}
    </button>
  )
}

Button.propTypes = {
  loading: PropTypes.bool,
  prefix: PropTypes.node,
  suffix: PropTypes.node,
  children: PropTypes.node,
  warning: PropTypes.bool,
  ghost: PropTypes.bool,
  border: PropTypes.bool,
  round: PropTypes.bool,
  full: PropTypes.bool,
  noborder: PropTypes.bool,
  sizing: PropTypes.oneOf(['default', 'medium', 'mini']),
  maxWidth: PropTypes.number,
  minWidth: PropTypes.number,
  noHover: PropTypes.bool,
  dotted: PropTypes.bool,
  error: PropTypes.bool,
  btnText: PropTypes.bool,
  customHover: PropTypes.bool,
  style: PropTypes.object,
  nobackground: PropTypes.bool,
  grey: PropTypes.bool,
  addClass: PropTypes.string,
  selected: PropTypes.bool,
  ascent: PropTypes.bool,
  textWarning: PropTypes.bool,
  warningbackgroundHover: PropTypes.bool,
  disabled: PropTypes.bool,
}

export default Button
